import { useEffect, useRef } from 'react'

import { ColumnType } from 'antd/lib/table'
import { useRouter } from 'next/router'

import { DocumentFolderRow } from '@/components/shared-components/DocumentFolders/DocumentFolderRow'
import { useProject } from '@/context/projectContext'

import {
  DocumentFolder,
  DocumentsListWithFoldersUiQuery,
} from 'types/api.graphql'

type Folders = DocumentsListWithFoldersUiQuery['documentFolders']

const FOLDER_ID_PREFIX = 'folder:'

export type FolderData = {
  folderId?: DocumentFolder['id']
  folderName?: DocumentFolder['name']
}

/**
 * Shoves the folder data into the start of the Ant table data provided
 * @param data normal data to be rendered in the table
 * @param folders the folders to be rendered in the table
 * @returns an array of data with the folders injected at the start
 */
export const injectFolderRows = <T extends object>(
  data: Array<T>,
  folders: Folders
): Array<T | FolderData> => {
  if (!folders?.length) return data

  const folderRows = folders.map<FolderData>((folder) => ({
    id: encodeFolderId(folder.id), // for the table row unique key
    folderId: folder.id,
    folderName: folder.name,
  }))

  return [...folderRows, ...data]
}

/**
 * Please forgive me for the code below. It detects if the row is a folder row and if so, renders the folder component in the first column and collapses all the other columns.
 * @param columns the normal Ant columns to be rendered in the table
 * @returns a cluster duck driving a cluster truck
 */
export const configureFolderColumns = (
  columns: Array<ColumnType<unknown>>
): Array<ColumnType<FolderData>> => {
  return columns.map((col, columnIndex) => ({
    ...col,
    onCell: (record) => {
      if (record.folderId) {
        return { colSpan: columnIndex === 0 ? columns.length : 0 } // if folder row, span the first colum across all columns and collapse all other columns
      }

      return {}
    },
    render: (_, record, index) => {
      if (record.folderId) {
        return columnIndex === 0 ? <DocumentFolderRow folder={record} /> : null // if folder row, render the folder component in the first column and `null` in all other columns
      }

      return col.render(_, record, index) // render the column as normal
    },
  }))
}

const QUERY_PARAM = 'folderId'

/**
 * Gives you almost everything you need to render folders within an Ant table and also
 * gives you access to the folderId in the query params.
 * @param clearFolderOnProjectChange if true, the folderId will be cleared from the query params when the project changes. TRY TO ONLY SET THIS TO TRUE ONCE PER PAGE PLEASE
 * @returns a bunch of helpful stuff
 */
export const useDocumentFolders = (clearFolderOnProjectChange: boolean) => {
  const currentProjectId = useRef<string>(undefined)

  const { project } = useProject()
  const { query, pathname, replace } = useRouter()

  const folderId = query[QUERY_PARAM] as string

  const setFolderId = (folderId?: string) => {
    if (!folderId) {
      replace({ pathname })
    } else {
      replace({ pathname, query: { folderId } })
    }
  }

  useEffect(() => {
    if (!clearFolderOnProjectChange) return

    if (!currentProjectId.current) {
      currentProjectId.current = project?.id
      return
    }

    if (currentProjectId.current !== project?.id) {
      currentProjectId.current = project?.id
      replace({ pathname })
    }
  }, [clearFolderOnProjectChange, project?.id, replace, pathname])

  return {
    folderId,
    setFolderId,
    injectFolderRows,
    configureFolderColumns,
  }
}

const encodeFolderId = (folderId: string) => {
  return `${FOLDER_ID_PREFIX}${folderId}`
}

export const decodeFolderId = (id: string) => {
  if (!id.startsWith(FOLDER_ID_PREFIX)) return id

  return id.replace(FOLDER_ID_PREFIX, '')
}

export const setFolderIdQueryParam = (url: string, folderId?: string) => {
  if (!folderId) return url

  return `${url}?${QUERY_PARAM}=${folderId}`
}

export const filterOutFolders = (ids: string[]) => {
  return ids.filter((id) => !id.startsWith(FOLDER_ID_PREFIX))
}
